import React, { useRef, useEffect } from 'react';
import { useFormikContext, useField } from 'formik';
import { FormControl, Input, InputLabel, OutlinedInput } from '@material-ui/core';
import { connect } from 'react-redux';
import dotProp from 'dot-prop';
import styled from 'styled-components/macro';

import FormHelperText from '../../internal-components/form-builder/FormHelperText';
import useTranslations from '../../hooks/useTranslations';
import debounce from 'lodash.debounce';

const FieldWrapper = styled.div`
  text-align: left;
`;

export const LengthMeter = styled.span`
  float: right;
  margin: 0 0 8px 8px;
  letter-spacing: 0.1ch;
  ${({ error }) => error && { fontWeight: 'bold' }}
`;

export function TextField({
  // states
  disabled,
  required,
  readOnly,
  hidden,

  // config
  state,
  dispatch,
  defaultValue,
  name,
  maxLength,
  fullWidth,
  InputLabelProps,
  inputProps,
  label,
  helperText,
  validate,
  wait = 0,

  // transform that lets number field force number values in Formik
  transformValue = x => x,
  ...props
}) {
  const { validateField, setFieldValue, setFieldTouched } = useFormikContext();

  const [appliedDefault, setAppliedDefault] = React.useState(false);

  const validateText = React.useCallback(
    value => {
      let error;

      if (!value && required) {
        error = 'forms.field_required';
      }

      if (maxLength && value && value.length > maxLength) {
        error = 'forms.textfield.toolong';
      }

      return error;
    },
    [maxLength, required]
  );

  let [{ value: formikValue }, meta] = useField({
    name,
    validate: validate || validateText
  });

  const { touched } = meta;
  const error = touched && meta.error;

  const t = useTranslations();

  const inputRef = useRef(null);

  // detects webkit autofill and triggers change event
  // thanks to https://github.com/mui-org/material-ui/issues/14427#issuecomment-466061244
  useEffect(() => {
    const input = inputRef.current;

    const autofillHandler = e => {
      if (e.animationName === 'onAutoFillStart' && input) {
        input.dispatchEvent(new CustomEvent('change'));
      }
    };

    input.addEventListener('animationstart', autofillHandler);

    return () => {
      if (input) {
        input.removeEventListener('animationstart', autofillHandler);
      }
    };
  }, []);

  const labelRef = React.useRef(null);

  // eslint-disable-next-line
  const [value, setValue] = React.useState(
    transformValue(formikValue || '') || ''
  );

  React.useEffect(() => {
    if (value !== formikValue) {
      setValue(formikValue);
    }
    // eslint-disable-next-line
  }, [formikValue]);

  if (defaultValue && !appliedDefault) {
    setTimeout(function() {
      setFieldValue(name, dotProp.get(state, defaultValue));
      setAppliedDefault(true);
    }, 500);
  }

  let lengthMeter;
  let tooLong = false;

  if (maxLength) {
    lengthMeter = `${value.length}/${maxLength}`;
    tooLong = value.length > maxLength;
  }

  const tooLongMessage = tooLong ? 'forms.textfield.toolong' : null;
  const helperTextContent = hidden
    ? helperText || ''
    : error || tooLongMessage || helperText || '';
  const helperTextValues = { maxLength };
  const hasError = (touched && Boolean(error) && !hidden) || tooLong;

  const InputMore = {};
  const variant = 'outlined';

  if (variant === 'outlined') {
    if (InputLabelProps && typeof InputLabelProps.shrink !== 'undefined') {
      InputMore.notched = InputLabelProps.shrink;
    }

    InputMore.labelWidth =
      (labelRef.current && labelRef.current.offsetWidth) || 0;
  }

  /**
   * We had a bug that when a text field is focused, it takes two clicks to trigger other elements in the form, e.g. checkboxes and <SaveProgressButton />. Delaying the blur callback seemes to mostly fix it.
   */
  const onBlur = () => setTimeout(() => setFieldTouched(name, true), 100);

  const isIE = /*@cc_on!@*/ false || !!document.documentMode;

  const onChangeDebounced = React.useCallback(
    debounce(
      v => {
        setFieldValue(name, transformValue(v || ''));
        setTimeout(() => {
          validateField(name);
          setFieldTouched(name, true);
        }, 100);
      },
      wait,
      { leading: !isIE, trailing: true }
    ),
    [wait]
  );

  const WhichInputClass = props['variant-override'] ? Input : OutlinedInput;

  return (
    <FieldWrapper>
      <FormControl
        style={{ width: '100%' }}
        error={hasError}
        fullWidth={fullWidth}
      >
        {label && (
          <InputLabel
            ref={labelRef}
            htmlFor="component-helper"
            style={{ top: '-6px', left: '13px', zIndex: 1 }}
          >
            {t(label)}
          </InputLabel>
        )}
        <WhichInputClass
          {...props}
          value={value || ''}
          onChange={e => {
            const v = e.target.value;
            setValue(v);
            onChangeDebounced(v);
          }}
          required={required}
          inputRef={inputRef}
          disabled={disabled}
          readOnly={readOnly}
          aria-describedby={helperText}
          {...InputMore}
          rowsMax={15}
          rows={3}
          onBlur={onBlur}
          inputProps={{
            ...inputProps,
            'data-testid': props['data-testid']
          }}
        />
        <FormHelperText id={helperText}>
          {lengthMeter && (
            <LengthMeter error={hasError}>{lengthMeter}</LengthMeter>
          )}
          {t(helperTextContent, helperTextValues)}
        </FormHelperText>
      </FormControl>
    </FieldWrapper>
  );
}

export default connect(state => ({ state }))(TextField);
